import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { UnderwritingActivity } from "@/models/underwriting";

type SortDirection = "asc" | "desc" | null;

export interface ActivityColumn {
  /** Stable key for the column. */
  id: string;
  /** Header label. */
  header: string;
  align?: "left" | "right";
  /** Extra classes for the cell (e.g. text colour). */
  cellClassName?: string;
  /** Renders the cell content for a row. */
  render: (activity: UnderwritingActivity) => React.ReactNode;
  /** When provided the column is sortable; returns a comparable number. */
  sortValue?: (activity: UnderwritingActivity) => number;
  /** Stop the row click from firing when interacting with the cell (e.g. buttons). */
  stopRowClick?: boolean;
}

export type SearchAccessor = (activity: UnderwritingActivity) => string | null | undefined;

const DEFAULT_SEARCH_ACCESSORS: SearchAccessor[] = [
  (a) => String(a.applicationId),
  (a) => a.customerName,
  (a) => a.affiliate,
  (a) => a.agentName,
];

interface UnderwritingActivityTableProps {
  activities: UnderwritingActivity[];
  columns: ActivityColumn[];
  onRowClick: (row: UnderwritingActivity) => void;
  /** Title shown above the table. */
  title?: string;
  /** Actions rendered on the right of the title row (e.g. a "View all" link). */
  headerActions?: React.ReactNode;
  /** Fields matched against the search box. Defaults to id/customer/affiliate/agent. */
  searchAccessors?: SearchAccessor[];
  emptyMessage?: string;
}

export function UnderwritingActivityTable({
  activities,
  columns,
  onRowClick,
  title,
  headerActions,
  searchAccessors = DEFAULT_SEARCH_ACCESSORS,
  emptyMessage = "No requests found.",
}: UnderwritingActivityTableProps) {
  const [search, setSearch] = useState("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const sortColumn = useMemo(() => columns.find((c) => c.sortValue), [columns]);

  const cycleSort = () =>
    setSortDirection((prev) => (prev === "desc" ? "asc" : prev === "asc" ? null : "desc"));

  const filtered = useMemo(() => {
    let result = activities;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((a) =>
        searchAccessors.some((accessor) => (accessor(a) ?? "").toLowerCase().includes(q))
      );
    }

    if (!sortColumn?.sortValue || sortDirection === null) return result;

    const getValue = sortColumn.sortValue;
    return [...result].sort((a, b) => {
      const diff = getValue(a) - getValue(b);
      return sortDirection === "asc" ? diff : -diff;
    });
  }, [activities, search, sortDirection, sortColumn, searchAccessors]);

  const sortTitle =
    sortDirection === "desc"
      ? "Sorted newest first — click to sort oldest first"
      : sortDirection === "asc"
      ? "Sorted oldest first — click to remove sorting"
      : "Not sorted — click to sort newest first";

  return (
    <div className="flex flex-col gap-3">
      {(title || headerActions) && (
        <div className="flex items-center justify-between">
          {title ? <h2 className="text-lg font-semibold text-foreground">{title}</h2> : <span />}
          {headerActions}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card shadow-sm">
        {/* Search */}
        <div className="px-4 py-3">
          <div className="relative w-72 max-w-full">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              className="pl-8 h-9 text-sm"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-y border-border">
                {columns.map((col) => {
                  const alignClass = col.align === "right" ? "text-right" : "text-left";
                  return (
                    <th
                      key={col.id}
                      className={cn(
                        "px-4 py-2.5 text-[12px] font-semibold text-muted-foreground whitespace-nowrap",
                        alignClass
                      )}
                    >
                      {col.sortValue ? (
                        <button
                          type="button"
                          onClick={cycleSort}
                          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                          title={sortTitle}
                        >
                          {col.header}
                          {sortDirection === "desc" ? (
                            <ChevronDown className="size-3.5" />
                          ) : sortDirection === "asc" ? (
                            <ChevronUp className="size-3.5" />
                          ) : (
                            <ChevronsUpDown className="size-3.5 opacity-50" />
                          )}
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-muted-foreground text-[11px]"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )}
              {filtered.map((activity) => (
                <tr
                  key={activity.quoteUnderwritingRequestId ?? `application-${activity.applicationId}`}
                  onClick={() => onRowClick(activity)}
                  className="h-[68px] border-b border-border last:border-0 hover:bg-slate-100 cursor-pointer transition-colors"
                >
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={cn(
                        "px-4 py-3 whitespace-nowrap",
                        col.align === "right" && "text-right",
                        col.cellClassName
                      )}
                      onClick={col.stopRowClick ? (e) => e.stopPropagation() : undefined}
                    >
                      {col.render(activity)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
